import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { payCardsApi, type PayCard } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { errorText } from '@/components/FlashMessage';
import { useToast } from '@/components/Feedback';

export function AdminPayCards() {
  const navigate = useNavigate();
  const { data: cards, loading, error: loadError } = useAsync(() => payCardsApi.list(), []);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const card = await payCardsApi.create({ name });
      setShowAdd(false);
      toast(`Draft pay card “${name}” created — add lane pay next`);
      setName('');
      navigate(`/app/admin/pay-cards/${card.id}`);
    } catch (err) {
      setError(errorText(err, 'Could not create the pay card.'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'name', header: 'Name' },
    {
      key: 'status',
      header: 'Status',
      value: (row: PayCard) => row.status,
      render: (row: PayCard) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'version',
      header: 'Version',
      value: (row: PayCard) => row.version ?? 1,
      render: (row: PayCard) => row.version ?? 1,
    },
  ];

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <p className="kicker">Crew pay</p>
            <h1>Pay cards</h1>
            <p className="text-muted">What drivers and helpers earn for each zone-to-zone trip, by vehicle type</p>
          </div>
          <Button onClick={() => setShowAdd(true)}>Add pay card</Button>
        </div>

        <DataTable
          columns={columns}
          data={cards ?? []}
          keyField="id"
          loading={loading}
          loadError={loadError}
          dense
          searchPlaceholder="Filter pay cards…"
          onRowClick={(row) => navigate(`/app/admin/pay-cards/${row.id}`)}
          emptyMessage="No pay cards yet. Click “Add pay card” to create a draft, add lane pay, then activate it."
        />

        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add pay card">
          <form onSubmit={handleAdd}>
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="2026 Crew pay"
            />
            {error && <p className="text-urgent">{error}</p>}
            <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: '1rem' }}>
              Cards start as draft. Add driver/helper amounts on the next screen, then activate.
            </p>
            <Button type="submit" loading={saving}>Create &amp; edit pay</Button>
          </form>
        </Modal>
      </div>
    </Layout>
  );
}
